// Sprint 6 HOCH 4 + BLOCKER 3 — Login/Opt-In-Modal mit Vorname + DSGVO.
// Sprint 7 (Slim) — Saubere Trennung „Anmelden" vs. „Registrieren":
//   mode="login"  → minimal (Email only) → POST /auth/magic/request (login_only=true)
//                   404 → Inline-Hinweis „Diese E-Mail ist nicht registriert" +
//                         „Jetzt registrieren"-Link wechselt intern auf mode="optin".
//   mode="optin"  → voll (Vorname + Email + DSGVO) → POST /auth/doi/request

import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { BRAND } from "@/config/brand";
// Sprint-V25 Meta-Pixel-Consolidation Hotfix (Sa 12.09.2026 spät):
// Lead-Event WAR hier — Andreas-Direktive: Lead darf nur bei bestaetigtem
// DOI feuern (auf /willkommen), nicht bei jeder Anforderung. Import daher
// entfernt, damit Meta nicht auf „interessiert, aber nie bestaetigt"
// optimiert. Trigger liegt jetzt exklusiv in WelcomePage.jsx.

const QUELLE_BY_MODE = {
  login: "login-modal",
  optin: "quick-check-ergebnis",
};

export default function LoginModal({
  open,
  onClose,
  mode = "login",
  fokustyp = null,
  source = null,
  onMagicRequested = null,
}) {
  // Sprint 7 — currentMode darf intern wechseln (Login → Register on 404),
  // ohne dass Parent-Komponente Mount-Key neu vergeben muss.
  const [currentMode, setCurrentMode] = useState(mode);
  const isOptIn = currentMode === "optin";
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [dsgvo, setDsgvo] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [doiRequired, setDoiRequired] = useState(false);
  const [error, setError] = useState(null);
  const [notRegistered, setNotRegistered] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCurrentMode(mode);
      setFirstName("");
      setEmail("");
      setDsgvo(false);
      setSending(false);
      setDone(false);
      setDoiRequired(false);
      setError(null);
      setNotRegistered(false);
      setTimeout(() => firstFieldRef.current?.focus(), 50);
    }
  }, [open, mode]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Sprint 7.1 Bugfix — LoginModal via Portal in document.body rendern.
  const renderModal = (children) =>
    typeof document !== "undefined"
      ? createPortal(children, document.body)
      : children;

  const switchToOptin = () => {
    setCurrentMode("optin");
    setError(null);
    setNotRegistered(false);
    // Email beibehalten, damit User sie nicht neu eintippen muss
    setTimeout(() => firstFieldRef.current?.focus(), 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setNotRegistered(false);
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = firstName.trim();

    if (isOptIn && !trimmedName) {
      setError("Bitte sag uns deinen Vornamen.");
      return;
    }
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setError("Bitte gib eine gültige E-Mail-Adresse ein.");
      return;
    }
    if (isOptIn && !dsgvo) {
      setError("Bitte stimme der Datenverarbeitung zu.");
      return;
    }

    setSending(true);
    try {
      if (isOptIn) {
        // V100.4 (17.09.2026) — session_id mitschicken, damit das Backend
        // beim DOI-Confirm den frueheren anonymen QuickCheck-Result finden
        // und primary_fokustyp nachtragen kann (Fallback-Attribution).
        let sessionId = null;
        try { sessionId = localStorage.getItem("f4s_session_id") || null; } catch { /* noop */ }
        const { data } = await api.post("/auth/doi/request", {
          email: trimmedEmail,
          first_name: trimmedName || trimmedEmail.split("@")[0],
          dsgvo: true,
          quelle: QUELLE_BY_MODE.optin,
          source,
          fokustyp,
          session_id: sessionId,
        });
        setDoiRequired(!!data?.doi_required);
        setDone(true);
        // Sprint-V25 Issue L (Sa 12.09.2026) — Landing-Analytics: test_start
        // wird nach erfolgreichem Magic-Link-Request gefeuert.
        // ANMERKUNG: Meta-Pixel `Lead` feuert hier bewusst NICHT — nur nach
        // bestaetigtem DOI (WelcomePage.jsx), damit Meta korrekt auf echte
        // Opt-Ins optimiert und nicht auf angefragte-aber-nie-bestaetigte.
        if (typeof onMagicRequested === "function") {
          try { onMagicRequested({ mode: "optin", email: trimmedEmail, source }); }
          catch (_e) { /* fire-and-forget */ }
        }
      } else {
        // Login-Mode → Magic-Link nur für bestehende User
        await api.post("/auth/magic/request", {
          email: trimmedEmail,
          source: source || "login-modal",
          login_only: true,
        });
        setDoiRequired(false);
        setDone(true);
        if (typeof onMagicRequested === "function") {
          try { onMagicRequested({ mode: "login", email: trimmedEmail, source }); }
          catch (_e) { /* fire-and-forget */ }
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (!isOptIn && (status === 404 || code === "email_not_registered")) {
        setNotRegistered(true);
      } else {
        const message =
          (typeof detail === "string" && detail) ||
          "Etwas ist schiefgelaufen. Bitte versuche es gleich nochmal.";
        setError(message);
      }
    } finally {
      setSending(false);
    }
  };

  // Sprint-V25 Issue L Hotfix (Sa 12.09.2026 10:15) — Metapher „Fahrplan"
  // → „Code" (Andreas-Direktive: passt zur Marke „FOKUSTYPEN CODE" im Footer,
  // triggert staerkere Curiosity).
  // 02.10.2026 — A/B-Optimierungs-Sprint (Hebel 1 „Framing"):
  //   Weg von „Konto anlegen / Code wartet" (Verwaltungs-Konnotation) →
  //   hin zu „Sichere dein persönliches Ergebnis" (Besitztums-Konnotation).
  //   Button-Text, Trust-Zeile und Headline sind nach Andreas-Spec exakt.
  const headline = isOptIn
    ? "Sichere dein persönliches Ergebnis in deinem PocketCoach"
    : "Willkommen zurück";
  const intro = isOptIn
    ? "Damit deine Auswertung, deine erkannten Muster und deine Gesprächshinweise nicht verloren gehen."
    : "Gib deine E-Mail ein — wir schicken dir einen Ein-Klick-Zugang zu deinem PocketCoach. Kein Passwort.";

  // Hebel 2 (Besitztumseffekt): Die gruene Hinweis-Box erscheint NUR,
  // wenn der User gerade einen QuickTest abgeschlossen hat (= `fokustyp`
  // Prop ist gesetzt). Beim reinen Register-Redirect ohne Test waere die
  // Box eine Luege.
  const showProfilReadyBox = isOptIn && Boolean(fokustyp);

  return renderModal(
    <div
      className="fixed inset-0 z-[100] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-modal-title"
      data-testid={isOptIn ? "optin-modal" : "login-modal"}
      data-mode={currentMode}
    >
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div
        className="relative min-h-full flex items-start sm:items-center justify-center p-4 sm:py-10"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-100 my-4 sm:my-0"
          data-testid="login-modal-card"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            data-testid="login-modal-close"
            className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {done ? (
            <div className="p-8 text-center" data-testid="login-modal-success">
              <div className="mx-auto inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-4">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h2
                id="login-modal-title"
                className="text-xl font-extrabold text-slate-900"
              >
                {doiRequired ? "Bestätige deine E-Mail" : "Check dein Postfach!"}
              </h2>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                {doiRequired ? (
                  <>
                    Wir haben dir eine E-Mail an{" "}
                    <span className="font-semibold text-slate-900">{email}</span>{" "}
                    geschickt. Klick auf den Bestätigungs-Link — danach bekommst
                    du sofort deinen Magic-Link für den Login.
                  </>
                ) : (
                  <>
                    Wir haben dir gerade einen Magic-Link an{" "}
                    <span className="font-semibold text-slate-900">{email}</span>{" "}
                    geschickt. Klick einfach drauf — fertig.
                  </>
                )}
              </p>
              <p className="mt-4 text-xs text-slate-500">
                Nichts im Postfach? Schau auch im Spam-Ordner nach.
              </p>
              <button
                type="button"
                onClick={onClose}
                data-testid="login-modal-success-close"
                className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
              >
                Schließen
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-700 mb-4">
                <Mail className="w-5 h-5" />
              </div>
              <h2
                id="login-modal-title"
                className="text-xl font-extrabold text-slate-900"
              >
                {headline}
              </h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{intro}</p>

              {/* 02.10.2026 — Hebel 2 (Besitztumseffekt). Grüne Box nur,
                  wenn gerade ein QuickTest-Ergebnis vorliegt (`fokustyp`). */}
              {showProfilReadyBox && (
                <div
                  className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-2.5"
                  data-testid="login-modal-profile-ready"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-emerald-900 leading-relaxed">
                    <b>Dein Fokustyp-Profil wurde generiert und für deinen PocketCoach vorbereitet.</b>
                    {" "}
                    Gib kurz deine E-Mail ein, damit wir deine Vorlagen dauerhaft für dich freischalten.
                  </p>
                </div>
              )}

              {isOptIn && (
                <>
                  <label
                    htmlFor="login-modal-firstname"
                    className="block mt-5 text-xs font-semibold uppercase tracking-wider text-slate-500"
                  >
                    Vorname
                  </label>
                  <input
                    ref={firstFieldRef}
                    id="login-modal-firstname"
                    type="text"
                    autoComplete="given-name"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Wie heißt du?"
                    data-testid="login-modal-firstname-input"
                    className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={sending}
                  />
                </>
              )}

              <label
                htmlFor="login-modal-email"
                className="block mt-5 text-xs font-semibold uppercase tracking-wider text-slate-500"
              >
                E-Mail-Adresse
              </label>
              <input
                ref={isOptIn ? undefined : firstFieldRef}
                id="login-modal-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (notRegistered) setNotRegistered(false);
                }}
                placeholder="deine@email.de"
                data-testid="login-modal-email-input"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                disabled={sending}
              />

              {isOptIn && (
                <label className="mt-4 flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dsgvo}
                    onChange={(e) => setDsgvo(e.target.checked)}
                    data-testid="login-modal-dsgvo-checkbox"
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    disabled={sending}
                  />
                  <span className="text-xs text-slate-600 leading-relaxed">
                    Ich stimme zu, dass {BRAND.name} mir per E-Mail Inhalte zu
                    Kommunikation und Fokustypen senden darf. Abmeldung jederzeit
                    über den Abmelde-Link in jeder Mail. Mehr unter{" "}
                    <a
                      href="https://effektiv-kommunizieren.de/datenschutz"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-slate-800 underline"
                    >
                      Datenschutz
                    </a>
                    .
                  </span>
                </label>
              )}

              {notRegistered && (
                <div
                  className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
                  data-testid="login-modal-not-registered"
                >
                  <p className="text-sm font-semibold text-amber-900">
                    Diese E-Mail ist nicht registriert.
                  </p>
                  <button
                    type="button"
                    onClick={switchToOptin}
                    data-testid="login-modal-switch-to-register"
                    className="mt-1 inline-flex items-center text-sm font-bold text-amber-900 hover:text-amber-700 underline"
                  >
                    Jetzt registrieren →
                  </button>
                </div>
              )}

              {error && !notRegistered && (
                <p
                  className="mt-3 text-sm text-red-600"
                  data-testid="login-modal-error"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={sending}
                data-testid="login-modal-submit"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wird gesendet…
                  </>
                ) : (
                  <>
                    {isOptIn ? "Meinen persönlichen PocketCoach öffnen" : "Magic-Link senden"}
                    <Mail className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* 02.10.2026 — Hebel 1 (Trust-Zeile unter dem Button).
                  Nur im opt-in-Flow, damit der Login-Flow weiter minimal bleibt. */}
              {isOptIn && (
                <p
                  className="mt-3 text-xs text-slate-500 leading-relaxed text-center"
                  data-testid="login-modal-trust-line"
                >
                  Kostenfrei. Sofort im Browser startklar. Kein Passwort-Krampf.
                </p>
              )}

              {!isOptIn && (
                <p className="mt-5 text-xs text-slate-500 leading-relaxed text-center">
                  Noch kein Account?{" "}
                  <button
                    type="button"
                    onClick={switchToOptin}
                    data-testid="login-modal-link-to-register"
                    className="font-semibold text-slate-700 underline hover:text-slate-900"
                  >
                    Jetzt registrieren
                  </button>
                </p>
              )}

              {isOptIn && mode === "login" && (
                <p className="mt-5 text-xs text-slate-500 leading-relaxed text-center">
                  Schon einen Account?{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentMode("login");
                      setError(null);
                      setNotRegistered(false);
                    }}
                    data-testid="login-modal-link-to-login"
                    className="font-semibold text-slate-700 underline hover:text-slate-900"
                  >
                    Zurück zu Anmelden
                  </button>
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
